/**
 * Neuron Report Dispatch — formatting.
 *
 * CONTRACT §8.7: every timestamp rendered to a user is IST and explicitly labelled.
 * There is no unlabelled time formatter in this file and there must not be one — if a
 * screen needs a bare "14:32" it is wrong, because an ops console read from three
 * timezones is how a stale window goes unnoticed.
 *
 * CONTRACT §8.6: never log a full phone number, borrower name, or token. The mask*
 * helpers below are the only sanctioned way to put any of those on screen or in a log.
 *
 * IST has no DST, so all civil-date arithmetic here is a fixed +05:30 shift rather than
 * an Intl round-trip. That makes window resolution exact and independent of the browser
 * timezone, which matters because operators run this from laptops set to UTC.
 */

import type { EpochSeconds, IsoDate, IsoTimestamp, LinkState, ReportWindow } from './types';

export const IST_OFFSET_MINUTES = 330; // +05:30
export const IST_TZ = 'Asia/Kolkata';
export const IST_LABEL = 'IST';

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Anything a timestamp can arrive as: ISO string, epoch seconds, epoch ms, Date. */
export type TimeInput = string | number | Date | null | undefined;

/** IST civil (wall-clock) fields. */
export interface IstCivil {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Coerce any accepted timestamp shape to a Date, or null.
 *
 * Numbers below 1e11 are treated as epoch SECONDS (grants use seconds; contract §2).
 * 1e11 seconds is year 5138, and 1e11 ms is 1973 — no real value is ambiguous.
 * A bare `YYYY-MM-DD` is read as an IST midnight, not a UTC one, so a date-only
 * value never slips back a day for a browser west of India.
 */
export function toDate(value: TimeInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const ms = Math.abs(value) < 1e11 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const raw = value.trim();
  if (raw === '') return null;

  // All-digit strings are epochs shipped as text.
  if (/^\d{9,14}$/.test(raw)) return toDate(Number(raw));

  // Date-only: pin to IST midnight.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (dateOnly) {
    return istCivilToDate({
      year: Number(dateOnly[1]),
      month: Number(dateOnly[2]),
      day: Number(dateOnly[3]),
    });
  }

  // "2026-09-16 14:32:11" (no zone) is an IST wall clock from our own pipeline, and so is
  // "2026-09-16 13:29 IST" — the backend renders every human-facing stamp through
  // `nrd.windows.fmt_ist`, which is "%Y-%m-%d %H:%M" plus a literal " IST". Every field the
  // API names `*_ist` arrives in that shape. Without the suffix branch, `new Date()` is
  // reached and returns Invalid Date, so `covered_to_ist` would read as "no coverage time
  // reported" on every single partial run — the label would survive and the TIME in it
  // would silently vanish.
  const naive = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(\.\d+)?(?:\s+IST)?$/.exec(raw);
  if (naive) {
    return istCivilToDate({
      year: Number(naive[1]),
      month: Number(naive[2]),
      day: Number(naive[3]),
      hour: Number(naive[4]),
      minute: Number(naive[5]),
      second: naive[6] ? Number(naive[6]) : 0,
    });
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Break an instant into IST wall-clock fields. */
export function istCivil(value: TimeInput): IstCivil | null {
  const d = toDate(value);
  if (!d) return null;
  const shifted = new Date(d.getTime() + IST_OFFSET_MINUTES * MS_PER_MINUTE);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    weekday: shifted.getUTCDay(),
  };
}

/** Build an instant from IST wall-clock fields. Inverse of istCivil(). */
export function istCivilToDate(parts: {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}): Date {
  const utcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );
  return new Date(utcMs - IST_OFFSET_MINUTES * MS_PER_MINUTE);
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

// ---------------------------------------------------------------------------
// Rendering — every one of these carries the IST label or is a bare calendar date
// ---------------------------------------------------------------------------

/** `16 Sep 2026`. A calendar date carries no clock, so it needs no zone label. */
export function istDate(value: TimeInput, fallback = '—'): string {
  const c = istCivil(value);
  if (!c) return fallback;
  return `${pad2(c.day)} ${MONTHS[c.month - 1]} ${c.year}`;
}

/** `Wed 16 Sep 2026`. */
export function istDateWithWeekday(value: TimeInput, fallback = '—'): string {
  const c = istCivil(value);
  if (!c) return fallback;
  return `${DAYS[c.weekday]} ${pad2(c.day)} ${MONTHS[c.month - 1]} ${c.year}`;
}

/** `14:32 IST`. */
export function istTime(value: TimeInput, fallback = '—'): string {
  const c = istCivil(value);
  if (!c) return fallback;
  return `${pad2(c.hour)}:${pad2(c.minute)} ${IST_LABEL}`;
}

/** `16 Sep 2026, 14:32 IST` — the default for anything with a clock. */
export function istDateTime(value: TimeInput, fallback = '—'): string {
  const c = istCivil(value);
  if (!c) return fallback;
  return `${pad2(c.day)} ${MONTHS[c.month - 1]} ${c.year}, ${pad2(c.hour)}:${pad2(c.minute)} ${IST_LABEL}`;
}

/** `16 Sep 2026, 14:32:11 IST` — for audit rows, where the second matters. */
export function istDateTimeExact(value: TimeInput, fallback = '—'): string {
  const c = istCivil(value);
  if (!c) return fallback;
  return `${pad2(c.day)} ${MONTHS[c.month - 1]} ${c.year}, ${pad2(c.hour)}:${pad2(c.minute)}:${pad2(c.second)} ${IST_LABEL}`;
}

/** `2026-09-16` — IST civil date, the machine form used in query strings and source keys. */
export function istDayKey(value: TimeInput): IsoDate | null {
  const c = istCivil(value);
  if (!c) return null;
  return `${c.year}-${pad2(c.month)}-${pad2(c.day)}`;
}

/** Today's IST civil date. */
export function istToday(now: Date = new Date()): IsoDate {
  return istDayKey(now) as IsoDate;
}

/** `16 Sep 2026 → 18 Sep 2026 IST`, collapsing to one date when from === to. */
export function istDateRange(from: TimeInput, to: TimeInput): string {
  const a = istDate(from, '');
  const b = istDate(to, '');
  if (!a && !b) return '—';
  if (!b || a === b) return `${a} ${IST_LABEL}`;
  if (!a) return `through ${b} ${IST_LABEL}`;
  return `${a} → ${b} ${IST_LABEL}`;
}

/** Clock in the top bar: `16 Sep 2026, 14:32 IST`. */
export function nowIstLabel(now: Date = new Date()): string {
  return istDateTime(now);
}

/**
 * `4 min ago` / `in 2 h`. A supplement, never a replacement: always pair it with an
 * absolute IST value (put the absolute in a `title=` at minimum) so nobody has to
 * guess what "2 d ago" resolves to.
 */
export function timeAgo(value: TimeInput, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) return '—';
  const deltaMs = now.getTime() - d.getTime();
  const future = deltaMs < 0;
  const abs = Math.abs(deltaMs);

  const say = (text: string): string => (future ? `in ${text}` : `${text} ago`);

  if (abs < 45_000) return future ? 'in a moment' : 'just now';
  if (abs < 90_000) return say('1 min');
  const mins = Math.round(abs / 60_000);
  if (mins < 60) return say(`${mins} min`);
  const hours = Math.round(abs / 3_600_000);
  if (hours < 24) return say(`${hours} h`);
  const days = Math.round(abs / MS_PER_DAY);
  if (days < 14) return say(`${days} d`);
  return istDate(d);
}

/** Absolute IST plus relative, for a single cell: `16 Sep 2026, 14:32 IST · 4 min ago`. */
export function istDateTimeWithAgo(value: TimeInput, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) return '—';
  return `${istDateTime(d)} · ${timeAgo(d, now)}`;
}

// ---------------------------------------------------------------------------
// Window resolution (contract §3) — mirrors the dispatcher so dry-run previews match
// ---------------------------------------------------------------------------

export interface ResolvedWindow {
  from: IsoDate;
  to: IsoDate;
  /** Inclusive day count. `same_day` is 1, not 0. */
  days: number;
}

function addDays(date: IsoDate, delta: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(y, m - 1, d) + delta * MS_PER_DAY);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

/** Inclusive whole-day span between two IST civil dates. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const diff = Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd);
  return Math.floor(diff / MS_PER_DAY) + 1;
}

/**
 * Resolve a schedule window to IST civil dates, exactly as the dispatcher does at fire
 * time. Screens use this for the "this will read N days" preview and for the §3
 * detailed-shape guardrail, so a run that the backend would refuse is refused here first.
 */
export function resolveWindow(window: ReportWindow, now: Date = new Date()): ResolvedWindow {
  const today = istToday(now);
  const yesterday = addDays(today, -1);

  switch (window.type) {
    case 'same_day':
      return { from: today, to: today, days: 1 };
    case 'yesterday':
      return { from: yesterday, to: yesterday, days: 1 };
    case 'last_n_days': {
      const n = Math.max(1, Math.floor(window.n || 1));
      const from = addDays(yesterday, -(n - 1));
      return { from, to: yesterday, days: n };
    }
    case 'mtd': {
      const c = istCivil(now)!;
      const from = `${c.year}-${pad2(c.month)}-01`;
      return { from, to: today, days: daysBetween(from, today) };
    }
    case 'from_date':
      return { from: window.from, to: today, days: daysBetween(window.from, today) };
    case 'custom':
      return { from: window.from, to: window.to, days: daysBetween(window.from, window.to) };
    default: {
      // Unknown window type from a newer backend: degrade to today rather than crash.
      return { from: today, to: today, days: 1 };
    }
  }
}

/** Human wording for a window, for tables and confirmation copy. */
export function describeWindow(window: ReportWindow): string {
  switch (window.type) {
    case 'same_day':
      return 'Same day';
    case 'yesterday':
      return 'Yesterday';
    case 'last_n_days':
      return `Last ${window.n} day${window.n === 1 ? '' : 's'} (ending yesterday)`;
    case 'mtd':
      return 'Month to date';
    case 'from_date':
      return `From ${istDate(window.from)}`;
    case 'custom':
      return `${istDate(window.from)} → ${istDate(window.to)}`;
    default:
      return 'Unknown window';
  }
}

/** Window wording plus the dates it resolves to right now. */
export function describeWindowResolved(window: ReportWindow, now: Date = new Date()): string {
  const r = resolveWindow(window, now);
  return `${describeWindow(window)} · ${istDateRange(r.from, r.to)} · ${r.days} day${r.days === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Source coverage (CONTRACT-COVERAGE, amending §8.3) — what the file actually held
// ---------------------------------------------------------------------------

/**
 * Every screen that states a run's coverage goes through `coverageOf`. One reader, one set
 * of words, because "every surface states the same true thing" is the whole point of the
 * feature and a sentence written twice is a sentence that drifts.
 *
 * THE VOCABULARY IS THE BACKEND'S, NOT OURS. `nrd.coverage` names the states `full`,
 * `partial` and `unmeasured`, writes `window_partial` / `covered_to_ist` /
 * `rows_misaligned` onto the run row, and calls the thing a reader sees a PART DAY — the
 * subject reads `16 Sep 2026 (part day, to 13:29 IST)` and the workbook cell reads
 * `PART DAY`. The console says "part day" too. An operator reading the mail and the
 * console side by side must not have to work out that they are the same thing.
 *
 * `partial` is also an overloaded word in this codebase, and the other meaning is the
 * opposite problem: the Downloads screen and the rollup both say "partial read" and mean a
 * TRUNCATED read of the S3 object. A part day is a complete read of a file that stops
 * early. That is why the rendered noun is "part day" and never a bare "partial".
 *
 * On the three states, and why there are three rather than two:
 *
 *   full        the data runs to the window end. Behaves exactly as before this feature.
 *   partial     the data stops early. Say so, and say when.
 *   unmeasured  nothing was measured. NOT the same as full.
 *
 * `unmeasured` is reached two ways, and both are real: a source with no time-of-day column
 * (four of the eight clients ship one and four do not), and any path that did not read the
 * data — a dry run, a resend, a stale gate. A console that rendered either as "covers the
 * whole day" would be making exactly the unlabelled claim this feature exists to prevent.
 * So `unmeasured` never asserts completeness: in a dense cell it says nothing at all
 * (silence is not a claim), and on a detail surface it says "not measured" in words.
 *
 * NOTE, deliberately absent: there is no "N hours short" figure anywhere here. A window
 * ends at midnight but the day's calling does not, so subtracting `covered_to` from the
 * window end produces a number that looks precise and means nothing. fusion 16 Sep would
 * read "10h 30m short" when the real shortfall against the last call of the day was
 * 4h 24m. State the coverage; never estimate the gap.
 */
export type CoverageState = 'full' | 'partial' | 'unmeasured';

/**
 * The coverage fields as the API actually sends them (`services/api/runs.py::public_run`
 * and `services/api/downloads.py::_download_item`).
 *
 * `covered_to_ist` is a RENDERED string — `2026-09-16 13:29 IST` — not an ISO instant, and
 * it is `""` rather than null when there is nothing to report. The ISO `covered_to` exists
 * on the stored run row but is not in the public allowlist, so this is the only clock the
 * console gets; `toDate` knows the format.
 */
export interface CoverageInput {
  coverage_state?: string | null;
  coverage_measured?: boolean | null;
  window_partial?: boolean | null;
  covered_to_ist?: string | null;
  rows_misaligned?: number | null;
  rows_read?: number | null;
}

export interface Coverage {
  state: CoverageState;
  /** Shorthand for `state === 'partial'`, for the many `x ? … : …` call sites. */
  isPartial: boolean;
  /** True whenever there is something to say — a part day, or a measured full one. */
  measured: boolean;
  coveredTo: Date | null;
  /** `16 Sep 2026, 13:29 IST`, or '' when nothing was measured. */
  coveredToLabel: string;
  /**
   * Rows the reader could not line up against the header, so their values sat under the
   * wrong column names. Measured at 70,006 of 185,115 (37.8%) in fusion's 13 Sep export,
   * which carries two row shapes under one header. `null` means the backend reported no
   * count at all — which must NOT render the same as a clean file.
   */
  rowsMisaligned: number | null;
  /**
   * Every row the reader walked, before any schedule filter — the only honest denominator
   * for `rowsMisaligned`. `rows_in` counts what survived the flow/campaign filter, so
   * dividing by that reported more misaligned rows than rows on a filtered schedule.
   */
  rowsRead: number | null;
  /** One sentence for a tooltip or a callout. Always true of this run, never empty. */
  summary: string;
}

function asState(value: unknown): CoverageState | null {
  return value === 'full' || value === 'partial' || value === 'unmeasured' ? value : null;
}

/**
 * Read coverage off anything that carries it — a run, a download row. Tolerates every
 * field being absent, which is what a run written before this feature shipped, the mock
 * API and the replayed live capture all look like.
 */
export function coverageOf(source: CoverageInput | null | undefined): Coverage {
  const coveredTo = toDate(source?.covered_to_ist ?? null);
  const raw = source?.rows_misaligned;
  const rowsMisaligned = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  const rawRead = source?.rows_read;
  const rowsRead = typeof rawRead === 'number' && Number.isFinite(rawRead) ? rawRead : null;

  // `coverage_state` is the backend's own verdict and outranks anything derived here.
  // Deriving is the fallback for a row written before the field existed: a run can be
  // flagged partial without a usable clock (the reader saw the data stop but could not put
  // a time on it), and that is still a part day — the label just says the time is missing.
  const declared = asState(source?.coverage_state);
  const state: CoverageState =
    declared ?? (source?.window_partial === true ? 'partial' : coveredTo ? 'full' : 'unmeasured');
  const coveredToLabel = coveredTo ? istDateTime(coveredTo) : '';

  const summary =
    state === 'partial'
      ? coveredTo
        ? `Part day — this export covers calls dialled up to ${coveredToLabel}. Calls dialled after that time are not in it.`
        : 'Part day — the data stops before the window it claims to cover, and no coverage time was reported.'
      : state === 'full'
        ? coveredTo
          ? `Complete — the export was taken after this window closed, and its last call is ${coveredToLabel}.`
          : 'Complete — the export was taken after this window closed.'
        : 'Coverage not measured — nothing is known about how far into the window this data reaches. That is not the same as covering all of it.';

  return {
    state,
    isPartial: state === 'partial',
    measured: state !== 'unmeasured',
    coveredTo,
    coveredToLabel,
    rowsMisaligned,
    rowsRead,
    summary,
  };
}

/**
 * The TRUE coverage, worded to stand in place of a requested window.
 *
 * `16 Sep 2026, up to 13:29 IST` for a single day, `01 Sep 2026 → 14 Sep 2026, up to
 * 17:11 IST` when the export stopped a whole day short of what was asked for — note the
 * END DATE is the covered one, not the requested one. Showing the requested end date
 * beside an earlier clock would still overstate the file.
 */
export function istCoverageRange(from: TimeInput, to: TimeInput, coveredTo: TimeInput): string {
  const end = istCivil(coveredTo);
  if (!end) return istDateRange(from, to);
  const clock = `${pad2(end.hour)}:${pad2(end.minute)}`;
  const endLabel = `${pad2(end.day)} ${MONTHS[end.month - 1]} ${end.year}`;
  const startKey = istDayKey(from);
  const endKey = `${end.year}-${pad2(end.month)}-${pad2(end.day)}`;
  if (!startKey || startKey === endKey) return `${endLabel}, up to ${clock} ${IST_LABEL}`;
  return `${istDate(from)} → ${endLabel}, up to ${clock} ${IST_LABEL}`;
}

/**
 * What a "Window (IST)" cell should read for this run: the true coverage when the run is a
 * part day, the requested window otherwise. The rule is literal — show the true coverage IN
 * PLACE OF the requested window — so the substitution happens here once rather than in six
 * `{isPartial ? … : …}` ternaries across five screens.
 */
export function coverageWindowLabel(coverage: Coverage, from: TimeInput, to: TimeInput): string {
  if (coverage.isPartial && coverage.coveredTo) {
    return istCoverageRange(from, to, coverage.coveredTo);
  }
  return istDateRange(from, to);
}

/**
 * Misaligned rows, said plainly, because an operator deciding whether to forward a file
 * needs the reason and not the jargon. Returns '' when the count is absent or zero — an
 * absent count must not read as a clean file, so callers that have room pair this with an
 * explicit `rowsMisaligned === null` branch.
 */
export function misalignedNote(coverage: Coverage): string {
  const n = coverage.rowsMisaligned;
  if (n === null || n <= 0) return '';
  // Against rows READ, never rows_in. rows_in is post-filter, so on a flow- or
  // campaign-filtered schedule this read "70,006 of 12,000 (583.4%)" — a number that
  // destroys confidence in the warning it is attached to.
  const total = coverage.rowsRead;
  const share =
    total !== null && total > 0
      ? ` of ${formatCount(total)} (${formatPercent(n / total, { digits: 1 })})`
      : '';
  return `${formatCount(n)} row${n === 1 ? '' : 's'}${share} could not be lined up against the header — their values sit under the wrong column names, so every figure derived from them is suspect.`;
}

// ---------------------------------------------------------------------------
// Link state (contract §1) — our expires_at, never the S3 signature
// ---------------------------------------------------------------------------

export interface LinkStatus {
  state: LinkState;
  /** `Expires in 3 d` / `Expired 2 d ago` / `Revoked 16 Sep 2026, 14:32 IST`. */
  label: string;
  /** Whole hours remaining; negative once expired. */
  hoursRemaining: number;
}

/**
 * Derive the state of a grant. `expiring` is the last 24 hours — a distinct state so
 * an operator can re-issue before a client hits a 410, rather than after.
 */
export function linkState(
  grant: { expires_at?: EpochSeconds | null; revoked_at?: EpochSeconds | null },
  now: Date = new Date(),
): LinkStatus {
  if (grant.revoked_at) {
    return {
      state: 'revoked',
      label: `Revoked ${istDateTime(grant.revoked_at)}`,
      hoursRemaining: 0,
    };
  }
  if (!grant.expires_at) {
    return { state: 'expired', label: 'No expiry recorded', hoursRemaining: 0 };
  }
  const expiresMs = grant.expires_at * 1000;
  const deltaMs = expiresMs - now.getTime();
  const hours = Math.floor(deltaMs / 3_600_000);

  if (deltaMs <= 0) {
    return {
      state: 'expired',
      label: `Expired ${timeAgo(expiresMs, now)}`,
      hoursRemaining: hours,
    };
  }
  if (deltaMs <= MS_PER_DAY) {
    return {
      state: 'expiring',
      label: `Expires in ${hours <= 1 ? '<1 h' : `${hours} h`}`,
      hoursRemaining: hours,
    };
  }
  const days = Math.floor(deltaMs / MS_PER_DAY);
  return {
    state: 'live',
    label: `Expires in ${days} d`,
    hoursRemaining: hours,
  };
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * Row counts, attempts, anything countable. Indian grouping (`2,80,00,000`) because
 * every operator reading this console reads lakh/crore natively.
 */
export function formatCount(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  return new Intl.NumberFormat('en-IN').format(value);
}

/** `₹1,20,000` — ptp_amount and committed_amount. */
export function formatInr(value: number | string | null | undefined, fallback = '—'): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

/** `62.4%`. Pass a ratio (0.624) or set `alreadyPercent`. */
export function formatPercent(
  value: number | null | undefined,
  { digits = 1, alreadyPercent = false, fallback = '—' } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  const pct = alreadyPercent ? value : value * 100;
  return `${pct.toFixed(digits)}%`;
}

/** Ratio of two counts, guarding the zero denominator: `formatRatio(120, 0)` → `—`. */
export function formatRatio(numerator: number, denominator: number, digits = 1): string {
  if (!denominator) return '—';
  return formatPercent(numerator / denominator, { digits });
}

/** `1.4 MB`. Binary units, because these are S3 object sizes. */
export function formatBytes(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  if (value === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(Math.abs(value)) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** exp;
  const digits = exp === 0 ? 0 : scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
  return `${scaled.toFixed(digits)} ${units[exp]}`;
}

/** Talk time: `48s`, `2m 05s`, `1h 04m`. */
export function formatDuration(seconds: number | string | null | undefined, fallback = '—'): string {
  const n = typeof seconds === 'string' ? Number(seconds) : seconds;
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return fallback;
  const total = Math.round(n);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return `${m}m ${pad2(s)}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${pad2(m % 60)}m`;
}

// ---------------------------------------------------------------------------
// Masking (contract §8.6) — full phone, borrower name and token never leave this file intact
// ---------------------------------------------------------------------------

/** Last 4 characters, or as many as exist. */
export function lastFour(value: string | null | undefined): string {
  if (!value) return '';
  const s = String(value);
  return s.length <= 4 ? s : s.slice(-4);
}

/** `••••4821`. The only sanctioned way to render a phone number. */
export function maskPhone(value: string | number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return fallback;
  return `••••${lastFour(digits)}`;
}

/** `…9f3a`. Tokens and token hashes. Never render or log the whole value. */
export function maskToken(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  return `…${lastFour(value)}`;
}

/** `R•••• K••••` — initials only. For logs and any screenshot-able surface. */
export function maskName(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  return parts.map((p) => `${p[0]?.toUpperCase() ?? ''}••••`).join(' ');
}

/** `ops@vinfer.ai` → `o••@vinfer.ai`. Recipient emails in logs; screens may show them in full. */
export function maskEmail(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const at = value.indexOf('@');
  if (at <= 0) return '•••';
  const local = value.slice(0, at);
  const head = local.slice(0, 1);
  return `${head}••${value.slice(at)}`;
}

/**
 * Console-safe logging. Anything passed through here has already been masked by the
 * caller; this exists so log calls are greppable and uniformly prefixed.
 */
export function safeLog(scope: string, message: string, fields?: Record<string, unknown>): void {
  if (fields) {
    // eslint-disable-next-line no-console
    console.info(`[nrd:${scope}] ${message}`, fields);
  } else {
    // eslint-disable-next-line no-console
    console.info(`[nrd:${scope}] ${message}`);
  }
}

// ---------------------------------------------------------------------------
// Strings
// ---------------------------------------------------------------------------

/** Middle-free truncation with an ellipsis. */
export function truncate(value: string | null | undefined, max = 60, fallback = '—'): string {
  if (!value) return fallback;
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** `reports/fusion/fusion_call_report_2026-09-15_to_2026-09-15.csv` → the filename. */
export function basename(key: string | null | undefined, fallback = '—'): string {
  if (!key) return fallback;
  const idx = key.lastIndexOf('/');
  return idx === -1 ? key : key.slice(idx + 1);
}

/**
 * Turn a source column name into a readable header WITHOUT touching its meaning.
 * Only ever used for column-policy pickers, never for disposition values — contract §5
 * is explicit that dispositions display exactly as written, no case-correcting, no
 * spelling fixes, no expansion.
 */
export function humaniseColumn(column: string): string {
  return column
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** `1 run` / `3 runs`. */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}

/** Stable, readable ordinal for list positions in the ranking editor. */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** ISO timestamp for API payloads. Instants on the wire are UTC; only display is IST. */
export function toIso(value: TimeInput): IsoTimestamp | null {
  const d = toDate(value);
  return d ? d.toISOString() : null;
}

// ---------------------------------------------------------------------------
// Run derivations shared by /runs, /runs/:id and /dashboard
//
// These live here, not on a screen, because the same number derived twice disagrees.
// /runs said "Month to date" and /dashboard said "01 Sep 2026 → 16 Sep 2026 IST" about
// the same run, and an operator reconciling the two had no way to know they agreed.
// ---------------------------------------------------------------------------

/**
 * A run still in flight after this long is not moving.
 *
 * Presentation only: it adds no status value, and RUN_STATUSES stays the closed,
 * validated vocabulary it is. It states a measured fact about the run beside the word.
 */
export const STUCK_MINUTES = 30;

/** Whole minutes between `value` and now. 0 when the timestamp is unusable. */
export function minutesSince(value: TimeInput, now: Date = new Date()): number {
  const date = toDate(value);
  if (!date) return 0;
  return Math.max(0, Math.round((now.getTime() - date.getTime()) / MS_PER_MINUTE));
}

/**
 * True when an in-flight run has not moved for longer than a run should take.
 *
 * `status` is passed rather than assumed so a settled run can never be called stalled:
 * only resolving / generating / sending can stall, and a run with no `started_at` is
 * never claimed to have stalled — we would be asserting an elapsed time we do not have.
 */
export function isStalledRun(
  status: string,
  startedAt: TimeInput,
  now: Date = new Date(),
): boolean {
  if (status !== 'resolving' && status !== 'generating' && status !== 'sending') return false;
  if (!toDate(startedAt)) return false;
  return minutesSince(startedAt, now) > STUCK_MINUTES;
}

/** `21 h 14 min`, `47 min` — the measured elapsed, for the line beside a stalled status. */
export function stalledFor(startedAt: TimeInput, now: Date = new Date()): string {
  const minutes = minutesSince(startedAt, now);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours > 0 ? `${days} d ${restHours} h` : `${days} d`;
}

/** What `runWindowLabel` needs off a run row. Every field is optional on the wire. */
export interface RunWindowInput extends CoverageInput {
  window_from?: IsoDate | null;
  window_to?: IsoDate | null;
  window_label?: string | null;
}

/**
 * What a run's "Window (IST)" cell reads: ONE grammar, the resolved IST range, always.
 *
 * For a part day it is the coverage the source actually had, not the window that was
 * asked for (CONTRACT-COVERAGE). `window_from` / `window_to` / `window_label` were added
 * to the run row — per their own docstring — precisely so a run list could stop showing a
 * bare spec word like "Month to date"; a column that produces four shapes cannot be
 * scanned, because the eye has to parse each cell instead of comparing positions.
 *
 * The spec word belongs in the cell's `title`, never in place of the dates.
 */
export function runWindowLabel(run: RunWindowInput | null | undefined): string {
  if (!run) return '—';
  const coverage = coverageOf(run);
  if (coverage.isPartial && coverage.coveredTo) {
    return coverageWindowLabel(coverage, run.window_from, run.window_to);
  }
  if (run.window_from && run.window_to) return istDateRange(run.window_from, run.window_to);
  if (run.window_label) return run.window_label;
  return '—';
}
